/**
 * Mentor/Client Service
 *
 * Frontend service for managing mentor-client relationships.
 * Wraps Cloud Function calls for client provisioning, removal, and management.
 */

import { httpsCallable } from 'firebase/functions';
import { functions, auth } from '../firebase/firebase';
import { getUserProfile } from './userService';
import enTranslations from '../translations/en.json';
import esTranslations from '../translations/es.json';

// Translation files mapped by language code
const translationsMap = {
  en: enTranslations,
  es: esTranslations
};

// Cache for language preference to avoid repeated Firestore calls
let languageCache = null;
let languageCacheTimestamp = 0;
const CACHE_DURATION = 1800000; // 30 minutes cache - language changes are infrequent

// Listen for localStorage changes to invalidate cache when language changes
// This ensures error messages are in the correct language immediately after user changes preference
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key === 'preferredLanguage') {
      // Language changed in another tab or context, invalidate cache
      languageCache = null;
      languageCacheTimestamp = 0;
    }
  });
}

/**
 * Invalidate language cache - useful when language changes in current context
 * This ensures error messages immediately reflect the new language preference
 * @export For use by other modules when language changes
 */
export function invalidateLanguageCache() {
  languageCache = null;
  languageCacheTimestamp = 0;
}

/**
 * Get user language preference following the same hierarchy as LanguageContext:
 * 1. Firestore user profile (if logged in) - SOURCE OF TRUTH
 * 2. Stored selection (localStorage)
 * 3. Browser's default language (if Spanish, use Spanish; otherwise English)
 * @returns {Promise<string>} Language code ('en' or 'es')
 */
async function getUserLanguage() {
  // Check cache first (valid for 30 minutes)
  const now = Date.now();
  if (languageCache && (now - languageCacheTimestamp) < CACHE_DURATION) {
    return languageCache;
  }

  try {
    // 1. Try to get from Firestore if user is logged in (SOURCE OF TRUTH)
    if (auth && auth.currentUser) {
      try {
        const userProfile = await getUserProfile(auth.currentUser.uid);
        if (userProfile && userProfile.language && (userProfile.language === 'en' || userProfile.language === 'es')) {
          languageCache = userProfile.language;
          languageCacheTimestamp = now;
          // Sync to localStorage for consistency (same as LanguageContext does)
          if (typeof window !== 'undefined') {
            localStorage.setItem('preferredLanguage', userProfile.language);
          }
          return userProfile.language;
        }
      } catch (error) {
        console.error('Error fetching language from Firestore:', error);
        // Fall through to localStorage fallback
      }
    }
    
    // 2. Fallback to localStorage
    if (typeof window !== 'undefined') {
      const savedLanguage = localStorage.getItem('preferredLanguage');
      if (savedLanguage === 'en' || savedLanguage === 'es') {
        languageCache = savedLanguage;
        languageCacheTimestamp = now;
        return savedLanguage;
      }
    }
    
    // 3. Fallback to browser language
    if (typeof window !== 'undefined') {
      const browserLang = navigator.language || navigator.userLanguage;
      const browserLangCode = browserLang.split('-')[0].toLowerCase();
      const defaultLanguage = browserLangCode === 'es' ? 'es' : 'en';
      languageCache = defaultLanguage;
      languageCacheTimestamp = now;
      if (typeof window !== 'undefined') {
        localStorage.setItem('preferredLanguage', defaultLanguage);
      }
      return defaultLanguage;
    }
    
    // Default to English if nothing else works
    languageCache = 'en';
    languageCacheTimestamp = now;
    return 'en';
  } catch (error) {
    console.error('Error getting user language:', error);
    // Fallback to localStorage on error
    if (typeof window !== 'undefined') {
      const savedLanguage = localStorage.getItem('preferredLanguage');
      if (savedLanguage === 'en' || savedLanguage === 'es') {
        languageCache = savedLanguage;
        languageCacheTimestamp = now;
        return savedLanguage;
      }
    }
    languageCache = 'en';
    languageCacheTimestamp = now;
    return 'en';
  }
}

/**
 * Get translation function for service files (outside React context)
 * Uses Firestore as source of truth for logged-in users, following LanguageContext pattern
 * @returns {Promise<Function>} Translation function t(key, defaultValue)
 */
async function getTranslationFunction() {
  const language = await getUserLanguage();
  const translations = translationsMap[language] || translationsMap.en;

  return (key, defaultValue = '') => {
    if (!translations) {
      return defaultValue || key;
    }

    const keys = key.split('.');
    let value = translations;

    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Key not found, return default or the key itself
        return defaultValue || key;
      }
    }

    return typeof value === 'string' ? value : (defaultValue || key);
  };
}

/**
 * Convert Firebase function errors to user-friendly messages
 * @param {Error} error - The error object from Firebase
 * @param {string} defaultMessage - Default message if error can't be mapped
 * @returns {Error} Error with user-friendly message
 */
async function handleFirebaseError(error, defaultMessageKey = 'mentor.errors.unexpectedError') {
  const t = await getTranslationFunction();
  
  // If it's already a user-friendly Error object with a message, return it
  if (error instanceof Error && error.message && !error.code) {
    // Check if the message is already user-friendly (doesn't contain technical terms)
    const technicalTerms = ['internal', 'error code', 'functions/', 'firebase'];
    const isTechnical = technicalTerms.some(term => 
      error.message.toLowerCase().includes(term)
    );
    if (!isTechnical) {
      return error;
    }
  }

  // Handle Firebase function error codes
  // Firebase errors can have code in different places depending on how they're thrown
  const errorCode = error.code || 
                    error.details?.code || 
                    (error.details && typeof error.details === 'string' ? error.details : null);
  
  // Extract error message from various possible locations
  const errorMessage = error.message || 
                       error.details?.message || 
                       (typeof error.details === 'string' ? error.details : null);

  // Map Firebase error codes to user-friendly messages
  switch (errorCode) {
    case 'unauthenticated':
      return new Error(t('mentor.errors.unauthenticated', 'You must be logged in to perform this action. Please log in and try again.'));
    
    case 'permission-denied':
      if (errorMessage && errorMessage.includes('Elite Annual')) {
        return new Error(t('mentor.errors.eliteAnnualRequiredUpgrade', 'This feature requires an Elite Annual subscription. Please upgrade to access client management.'));
      }
      return new Error(t('mentor.errors.permissionDenied', 'You do not have permission to perform this action. Please contact support if you believe this is an error.'));
    
    case 'not-found':
      return new Error(t('mentor.errors.notFound', 'The requested resource was not found. Please refresh the page and try again.'));
    
    case 'already-exists':
      return new Error(t('mentor.errors.alreadyExists', 'This client is already in your list.'));
    
    case 'invalid-argument':
      if (errorMessage) {
        // Use the error message if it's user-friendly
        if (errorMessage.includes('email') || errorMessage.includes('Email')) {
          return new Error(t('mentor.errors.invalidEmail', 'Please enter a valid email address.'));
        }
        return new Error(errorMessage);
      }
      return new Error(t('mentor.errors.invalidArgument', 'Invalid information provided. Please check your input and try again.'));
    
    case 'failed-precondition':
      return new Error(t('mentor.errors.failedPrecondition', 'Unable to complete this action. The system is in an invalid state. Please refresh and try again.'));
    
    case 'out-of-range':
      return new Error(t('mentor.errors.outOfRange', 'The operation is not allowed at this time. Please try again later.'));
    
    case 'unimplemented':
      return new Error(t('mentor.errors.unimplemented', 'This feature is not yet available. Please contact support for more information.'));
    
    case 'internal':
      return new Error(t('mentor.errors.internal', 'An unexpected error occurred on our end. Please try again in a moment. If the problem persists, contact support.'));
    
    case 'unavailable':
      return new Error(t('mentor.errors.unavailable', 'The service is temporarily unavailable. Please try again in a few moments.'));
    
    case 'deadline-exceeded':
      return new Error(t('mentor.errors.deadlineExceeded', 'The request took too long to process. Please try again.'));
    
    case 'resource-exhausted':
      return new Error(t('mentor.errors.resourceExhausted', 'Too many requests. Please wait a moment and try again.'));
    
    default:
      // Check if there's a meaningful error message
      if (errorMessage) {
        const lowerMessage = errorMessage.toLowerCase();
        
        // Filter out technical/internal error messages
        const technicalTerms = ['internal', 'error code', 'functions/', 'firebase', 'unexpected error'];
        const isTechnical = technicalTerms.some(term => lowerMessage.includes(term));
        
        if (!isTechnical) {
          // Message seems user-friendly, use it
          return new Error(errorMessage);
        }
      }
      
      // Check for network errors
      if (errorMessage && (errorMessage.includes('network') || errorMessage.includes('fetch'))) {
        return new Error(t('mentor.errors.networkError', 'Network error. Please check your internet connection and try again.'));
      }
      
      // Check for timeout errors
      if (errorMessage && (errorMessage.includes('timeout') || errorMessage.includes('deadline'))) {
        return new Error(t('mentor.errors.timeout', 'The request took too long. Please try again.'));
      }
      
      // Fallback to default message
      return new Error(t(defaultMessageKey, 'An unexpected error occurred. Please try again.'));
  }
}

/**
 * Provision a client for the current mentor
 * @param {string} clientEmail - Client's email address
 * @returns {Promise<Object>} Result with client user ID and status
 */
export const provisionClient = async (clientEmail) => {
  try {
    const provisionClientFn = httpsCallable(functions, 'provisionClient');
    const result = await provisionClientFn({ clientEmail });
    return result.data;
  } catch (error) {
    console.error('Error provisioning client:', error);
    throw await handleFirebaseError(error, 'mentor.errors.provisionClientFailed');
  }
};

/**
 * Remove a client from the current mentor
 * @param {string} clientUserId - Client's user ID
 * @returns {Promise<void>}
 */
export const removeClient = async (clientUserId) => {
  try {
    const removeClientFn = httpsCallable(functions, 'removeClient');
    await removeClientFn({ clientUserId });
  } catch (error) {
    console.error('Error removing client:', error);
    throw await handleFirebaseError(error, 'mentor.errors.removeClientFailed');
  }
};

/**
 * Get all clients for the current mentor
 * @returns {Promise<Array>} Array of client objects
 */
export const getMentorClients = async () => {
  try {
    console.log('[mentorClientService] Calling getMentorClients Cloud Function...');
    const getMentorClientsFn = httpsCallable(functions, 'getMentorClients');
    const result = await getMentorClientsFn();
    console.log('[mentorClientService] getMentorClients result:', result);
    return result.data || [];
  } catch (error) {
    console.error('[mentorClientService] Error fetching mentor clients:', error);
    console.error('[mentorClientService] Error code:', error.code);
    console.error('[mentorClientService] Error message:', error.message);
    console.error('[mentorClientService] Error details:', error.details);

    // Use the centralized error handler
    throw await handleFirebaseError(error, 'mentor.errors.loadClientsFailed');
  }
};

/**
 * Update client invitation status (accept or reject)
 * @param {string} mentorId - Mentor's user ID
 * @param {string} status - New status ('active' or 'rejected')
 * @returns {Promise<void>}
 */
export const updateClientInvitationStatus = async (mentorId, status) => {
  try {
    const updateStatusFn = httpsCallable(functions, 'updateClientInvitationStatus');
    await updateStatusFn({ mentorId, status });
  } catch (error) {
    console.error('Error updating invitation status:', error);
    throw await handleFirebaseError(error, 'mentor.errors.updateInvitationStatusFailed');
  }
};

/**
 * Update client information (name and notes)
 * @param {string} clientUserId - Client's user ID
 * @param {Object} updates - Object with firstName, lastName, and/or notes
 * @returns {Promise<void>}
 */
export const updateClientInfo = async (clientUserId, updates) => {
  try {
    const updateClientInfoFn = httpsCallable(functions, 'updateClientInfo');
    await updateClientInfoFn({ 
      clientUserId,
      nickname: updates.nickname,
      notes: updates.notes,
    });
  } catch (error) {
    console.error('Error updating client info:', error);
    throw await handleFirebaseError(error, 'mentor.errors.updateClientInfoFailed');
  }
};

/**
 * Check if current user is a super admin
 * Super admins are stored in a protected Firestore collection
 * @returns {Promise<Object>} Object with isSuperAdmin boolean and privileges
 */
export const checkSuperAdminStatus = async () => {
  try {
    const checkStatusFn = httpsCallable(functions, 'checkSuperAdminStatus');
    const result = await checkStatusFn();
    return result.data;
  } catch (error) {
    console.error('Error checking super admin status:', error);
    // Don't throw - just return not super admin
    return { isSuperAdmin: false, privileges: [] };
  }
};

/**
 * Create a test license for a client (bypasses Stripe payment)
 * Only super admins can use this function
 * @param {string} clientEmail - Client's email address
 * @returns {Promise<Object>} Result with client info and license details
 */
export const createTestClientLicense = async (clientEmail) => {
  try {
    const createTestLicenseFn = httpsCallable(functions, 'createTestClientLicense');
    const result = await createTestLicenseFn({ clientEmail });
    return result.data;
  } catch (error) {
    console.error('Error creating test license:', error);
    throw await handleFirebaseError(error, 'mentor.errors.createTestLicenseFailed');
  }
};

export default {
  provisionClient,
  removeClient,
  getMentorClients,
  updateClientInvitationStatus,
  updateClientInfo,
  checkSuperAdminStatus,
  createTestClientLicense,
};
